import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  MenuFormula,
  FormulaComponentItemOption,
  FormulaComponentItemOptionPrice,
} from "@/redux/services/menu/menu_formula.type";
import { MenuItemOptionValue } from "@/redux/services/menu/menu.type";
import {
  cn,
  displayPrice,
  getImageUrl,
} from "@/lib/utils";
import { Check, Minus, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

export interface FormulaSelection {
  componentId: string;
  componentItemId: string;
  itemName: string;
  menuItemId?: string;
  priceModifier: number;
  selectedOptionValueIds?: string[];
  /** Titles of the chosen option values (e.g. "Moyenne"), for the cart line. */
  selectedOptionLabels?: string[];
  optionsSurcharge?: number;
}

interface FormulaConfigModalProps {
  open: boolean;
  onClose: () => void;
  formula: MenuFormula | null;
  currency: string;
  onAdd: (
    quantity: number,
    additionalDemands: string,
    selections: FormulaSelection[]
  ) => void;
}

const FormulaConfigModal: React.FC<FormulaConfigModalProps> = ({
  open,
  onClose,
  formula,
  currency,
  onAdd,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [additionalDemands, setAdditionalDemands] = useState("");
  // selections keyed by componentId -> array of selected component item ids
  const [selections, setSelections] = useState<
    Record<string, string[]>
  >({});
  // option selections keyed by componentItemId -> optionId -> array of selected value ids
  const [optionSelections, setOptionSelections] = useState<
    Record<string, Record<string, string[]>>
  >({});

  const resetState = useCallback(() => {
    setQuantity(1);
    setAdditionalDemands("");
    setSelections({});
    setOptionSelections({});
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetState();
      onClose();
    }
  };

  const toggleSelection = (
    componentId: string,
    itemId: string,
    maxChoiceCount: number
  ) => {
    setSelections((prev) => {
      const current = prev[componentId] ?? [];
      const existingIndex = current.indexOf(itemId);

      if (existingIndex >= 0) {
        // Clear option selections for deselected item
        setOptionSelections((prevOpts) => {
          const updated = { ...prevOpts };
          delete updated[itemId];
          return updated;
        });
        return {
          ...prev,
          [componentId]: current.filter((id) => id !== itemId),
        };
      }

      if (maxChoiceCount === 1) {
        // Clear option selections for previously selected item
        const previousItemId = current[0];
        if (previousItemId && previousItemId !== itemId) {
          setOptionSelections((prevOpts) => {
            const updated = { ...prevOpts };
            delete updated[previousItemId];
            return updated;
          });
        }
        return { ...prev, [componentId]: [itemId] };
      }

      if (maxChoiceCount > 0 && current.length >= maxChoiceCount) {
        return prev;
      }

      return { ...prev, [componentId]: [...current, itemId] };
    });
  };

  const toggleOptionValue = (
    componentItemId: string,
    optionId: string,
    valueId: string,
    type: string,
    maxSelections: number
  ) => {
    setOptionSelections((prev) => {
      const itemOpts = prev[componentItemId] ?? {};
      const current = itemOpts[optionId] ?? [];

      if (type === "radio" || type === "select") {
        if (current.includes(valueId)) {
          return {
            ...prev,
            [componentItemId]: { ...itemOpts, [optionId]: [] },
          };
        }
        return {
          ...prev,
          [componentItemId]: { ...itemOpts, [optionId]: [valueId] },
        };
      }

      // Checkbox
      if (current.includes(valueId)) {
        return {
          ...prev,
          [componentItemId]: {
            ...itemOpts,
            [optionId]: current.filter((id) => id !== valueId),
          },
        };
      }
      if (maxSelections > 0 && current.length >= maxSelections) {
        return prev;
      }
      return {
        ...prev,
        [componentItemId]: {
          ...itemOpts,
          [optionId]: [...current, valueId],
        },
      };
    });
  };

  const selectOptionValue = (
    componentItemId: string,
    optionId: string,
    valueId: string
  ) => {
    setOptionSelections((prev) => {
      const itemOpts = prev[componentItemId] ?? {};
      return {
        ...prev,
        [componentItemId]: {
          ...itemOpts,
          [optionId]: valueId ? [valueId] : [],
        },
      };
    });
  };

  const getOptionValuePrice = (
    itemOption: FormulaComponentItemOption,
    valueId: string,
    componentItemId: string
  ): number => {
    // 1. Check itemOptionPrices (direct price modifier)
    const optionPrice = itemOption.itemOptionPrices.find(
      (p: FormulaComponentItemOptionPrice) =>
        p.menuItemOptionValue.id === valueId
    );
    if (optionPrice) {
      return optionPrice.priceModifier ?? 0;
    }

    const value = itemOption.menuItemOption.menuItemOptionValues.find(
      (v: MenuItemOptionValue) => v.id === valueId
    );
    if (!value) return 0;

    // 2. Template options: use value's basePrice
    if (itemOption.menuItemOption.isTemplate) {
      return value.basePrice ?? 0;
    }

    // 3. Dependent options: look up price based on parent option's selected value
    if (itemOption.menuItemOption.dependsOnOption) {
      const parentOptionId = itemOption.menuItemOption.dependsOnOption.id;
      const componentItem = formula?.components
        .flatMap((c) => c.includedItems ?? [])
        .find((i) => i.id === componentItemId);
      const parentItemOption = componentItem?.itemOptions?.find(
        (opt) => opt.menuItemOption.id === parentOptionId
      );
      if (parentItemOption) {
        const parentSelectedIds =
          optionSelections[componentItemId]?.[parentItemOption.id] ?? [];
        if (parentSelectedIds.length > 0) {
          const dependentPrice = value.valueDependentPrices?.find(
            (dp) => dp.dependsOnValue?.id === parentSelectedIds[0]
          );
          if (dependentPrice) {
            return dependentPrice.priceModifier ?? 0;
          }
        }
      }
    }

    return 0;
  };

  const getItemOptionsTotal = (componentItemId: string): number => {
    const item = formula?.components
      .flatMap((c) => c.includedItems ?? [])
      .find((i) => i.id === componentItemId);
    if (!item?.itemOptions) return 0;

    let total = 0;
    const itemOpts = optionSelections[componentItemId] ?? {};
    for (const opt of item.itemOptions) {
      const selected = itemOpts[opt.id] ?? [];
      for (const valueId of selected) {
        total += getOptionValuePrice(opt, valueId, componentItemId);
      }
    }
    return total;
  };

  const requiredMet = useMemo(() => {
    if (!formula) return false;

    // Check required components
    const componentsMet = formula.components
      .filter((c) => c.isRequired)
      .every((c) => (selections[c.id]?.length ?? 0) > 0);
    if (!componentsMet) return false;

    // Check required options for selected items
    for (const component of formula.components) {
      const selectedIds = selections[component.id] ?? [];
      for (const itemId of selectedIds) {
        const item = component.includedItems?.find((i) => i.id === itemId);
        if (item?.itemOptions) {
          const itemOpts = optionSelections[itemId] ?? {};
          for (const opt of item.itemOptions) {
            if (opt.isRequired && (itemOpts[opt.id]?.length ?? 0) === 0) {
              return false;
            }
          }
        }
      }
    }

    return true;
  }, [formula, selections, optionSelections]);

  // Calculate total supplement
  const totalSupplement = useMemo(() => {
    if (!formula) return 0;
    let supplement = 0;
    for (const component of formula.components) {
      const selectedIds = selections[component.id] ?? [];
      for (const itemId of selectedIds) {
        const item = component.includedItems?.find((i) => i.id === itemId);
        if (item) {
          supplement += item.priceModifier;
          supplement += getItemOptionsTotal(itemId);
        }
      }
    }
    return supplement;
  }, [formula, selections, optionSelections]);

  const totalPrice = formula ? (formula.price + totalSupplement) * quantity : 0;

  const handleAdd = () => {
    if (!formula || !requiredMet) return;

    const formulaSelections: FormulaSelection[] = [];
    for (const component of formula.components) {
      const selectedIds = selections[component.id] ?? [];
      for (const itemId of selectedIds) {
        const item = component.includedItems?.find((i) => i.id === itemId);
        if (item) {
          const itemOpts = optionSelections[itemId] ?? {};
          const allSelectedOptionValueIds = Object.values(itemOpts).flat();
          const optionsSurcharge = getItemOptionsTotal(itemId);
          const selectedOptionLabels = allSelectedOptionValueIds
            .map(
              (valueId) =>
                item.itemOptions
                  ?.flatMap((opt) => opt.menuItemOption.menuItemOptionValues)
                  .find((v) => v.id === valueId)?.title
            )
            .filter((title): title is string => !!title);

          formulaSelections.push({
            componentId: component.id,
            componentItemId: item.id,
            itemName: item.name ?? item.product?.title ?? "",
            priceModifier: item.priceModifier,
            selectedOptionValueIds:
              allSelectedOptionValueIds.length > 0
                ? allSelectedOptionValueIds
                : undefined,
            selectedOptionLabels:
              selectedOptionLabels.length > 0 ? selectedOptionLabels : undefined,
            optionsSurcharge: optionsSurcharge > 0 ? optionsSurcharge : undefined,
          });
        }
      }
    }

    onAdd(quantity, additionalDemands.trim(), formulaSelections);
    resetState();
    onClose();
  };

  if (!formula) return null;

  const sortedComponents = [...formula.components].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col p-0 gap-0 sm:max-w-lg">
        <DialogHeader className="p-5 pb-0">
          <div className="flex items-center gap-4">
            <img
              src={getImageUrl(formula.image?.url)}
              alt={formula.name}
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <DialogTitle className="text-base truncate">
                {formula.name}
              </DialogTitle>
              <DialogDescription className="text-sm font-semibold text-primary mt-0.5">
                {displayPrice(formula.price, currency)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Components selection */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {sortedComponents.map((component) => {
            const sortedItems = [...(component.includedItems ?? [])].sort(
              (a, b) => a.sortOrder - b.sortOrder
            );
            const selected = selections[component.id] ?? [];

            return (
              <div key={component.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold">
                    {component.name}
                  </h4>
                  {component.isRequired && (
                    <span className="text-[10px] font-semibold bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">
                      Obligatoire
                    </span>
                  )}
                  {component.maxChoiceCount > 1 && (
                    <span className="text-[10px] text-muted-foreground">
                      (max {component.maxChoiceCount})
                    </span>
                  )}
                </div>

                {component.description && (
                  <p className="text-xs text-muted-foreground">
                    {component.description}
                  </p>
                )}

                {sortedItems.length > 0 ? (
                  <div className="space-y-1">
                    {sortedItems.map((item) => {
                      const isSelected = selected.includes(item.id);
                      const itemName =
                        item.name ?? item.product?.title ?? "Sans nom";
                      const itemImage =
                        item.image?.url ?? item.product?.image?.url;
                      const expandedOptions = (item.itemOptions ?? []).filter(
                        (io) =>
                          typeof io.menuItemOption === "object" &&
                          io.menuItemOption?.title
                      );
                      const hasOptions =
                        isSelected && expandedOptions.length > 0;

                      return (
                        <div key={item.id}>
                          <button
                            onClick={() =>
                              toggleSelection(
                                component.id,
                                item.id,
                                component.maxChoiceCount
                              )
                            }
                            className={cn(
                              "w-full flex items-center justify-between p-3 rounded-lg border text-sm transition-all",
                              isSelected
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/30"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={cn(
                                  "w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                                  component.maxChoiceCount === 1
                                    ? "rounded-full"
                                    : "rounded",
                                  isSelected
                                    ? "border-primary bg-primary"
                                    : "border-border"
                                )}
                              >
                                {isSelected && (
                                  <Check size={12} className="text-white" />
                                )}
                              </div>
                              {itemImage && (
                                <img
                                  src={getImageUrl(itemImage)}
                                  alt={itemName}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              )}
                              <span
                                className={isSelected ? "font-medium" : ""}
                              >
                                {itemName}
                              </span>
                            </div>

                            {item.priceModifier > 0 && (
                              <span className="text-xs text-accent font-medium flex-shrink-0 ml-2">
                                +{displayPrice(item.priceModifier, currency)}
                              </span>
                            )}
                          </button>

                          {/* Inline option selection for selected items */}
                          {hasOptions && (
                            <div className="ml-7 mt-1 mb-2 pl-3 border-l-2 border-primary/20 space-y-3">
                              {item.itemOptions!
                                .filter(
                                  (io) =>
                                    typeof io.menuItemOption === "object" &&
                                    io.menuItemOption?.title
                                )
                                .map((itemOption) => {
                                const option = itemOption.menuItemOption;
                                const values =
                                  option.menuItemOptionValues ?? [];
                                const selectedVals =
                                  optionSelections[item.id]?.[itemOption.id] ??
                                  [];

                                return (
                                  <div
                                    key={itemOption.id}
                                    className="space-y-1"
                                  >
                                    <div className="flex items-center gap-2">
                                      <h5 className="text-xs font-semibold">
                                        {option.title}
                                      </h5>
                                      {itemOption.isRequired && (
                                        <span className="text-[9px] font-semibold bg-destructive/10 text-destructive px-1.5 py-0.5 rounded-full">
                                          Obligatoire
                                        </span>
                                      )}
                                      {itemOption.maxSelections > 1 && (
                                        <span className="text-[9px] text-muted-foreground">
                                          (max {itemOption.maxSelections})
                                        </span>
                                      )}
                                    </div>

                                    {option.type === "select" ? (
                                      <Select
                                        value={selectedVals[0] ?? ""}
                                        onValueChange={(val) =>
                                          selectOptionValue(
                                            item.id,
                                            itemOption.id,
                                            val
                                          )
                                        }
                                      >
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue placeholder="Choisir" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {values.map((value) => {
                                            const priceMod =
                                              getOptionValuePrice(
                                                itemOption,
                                                value.id,
                                                item.id
                                              );
                                            return (
                                              <SelectItem
                                                key={value.id}
                                                value={value.id}
                                              >
                                                {value.title}
                                                {priceMod > 0 &&
                                                  ` (+${displayPrice(priceMod, currency)})`}
                                              </SelectItem>
                                            );
                                          })}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <div className="space-y-0.5">
                                        {values.map((value) => {
                                          const isValSelected =
                                            selectedVals.includes(value.id);
                                          const priceMod =
                                            getOptionValuePrice(
                                              itemOption,
                                              value.id,
                                              item.id
                                            );
                                          const isRadio =
                                            option.type === "radio";

                                          return (
                                            <button
                                              key={value.id}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleOptionValue(
                                                  item.id,
                                                  itemOption.id,
                                                  value.id,
                                                  option.type,
                                                  itemOption.maxSelections
                                                );
                                              }}
                                              className={cn(
                                                "w-full flex items-center justify-between p-2 rounded-md border text-xs transition-all",
                                                isValSelected
                                                  ? "border-primary bg-primary/5"
                                                  : "border-border hover:border-primary/30"
                                              )}
                                            >
                                              <div className="flex items-center gap-1.5">
                                                <div
                                                  className={cn(
                                                    "w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                                                    isRadio
                                                      ? "rounded-full"
                                                      : "rounded-sm",
                                                    isValSelected
                                                      ? "border-primary bg-primary"
                                                      : "border-border"
                                                  )}
                                                >
                                                  {isValSelected && (
                                                    <Check
                                                      size={10}
                                                      className="text-white"
                                                    />
                                                  )}
                                                </div>
                                                <span
                                                  className={
                                                    isValSelected
                                                      ? "font-medium"
                                                      : ""
                                                  }
                                                >
                                                  {value.title}
                                                </span>
                                              </div>
                                              {priceMod > 0 && (
                                                <span className="text-[10px] text-muted-foreground">
                                                  +
                                                  {displayPrice(
                                                    priceMod,
                                                    currency
                                                  )}
                                                </span>
                                              )}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">
                    Aucun élément disponible
                  </p>
                )}
              </div>
            );
          })}

          {/* Notes */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Notes</h4>
            <textarea
              value={additionalDemands}
              onChange={(e) => setAdditionalDemands(e.target.value)}
              placeholder="Instructions spéciales..."
              rows={2}
              className="w-full rounded-lg border border-border p-3 text-sm placeholder:text-muted-foreground outline-none focus:border-primary transition-colors resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Quantité</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
              >
                <Minus size={16} />
              </button>
              <span className="text-sm font-semibold w-6 text-center">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <Button
            onClick={handleAdd}
            disabled={!requiredMet}
            className="w-full"
            size="lg"
          >
            Ajouter — {displayPrice(totalPrice, currency)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormulaConfigModal;
